import type { ComponentProps, ReactNode } from 'react'

import { formatDate } from 'date-fns'
import ReactIs from 'react-is'

import { cx } from '@/libs/cva'
import { toIdr } from '@/libs/utils/format'

interface CellContentProps extends ComponentProps<'div'> {}

export function CellContent({ className, ...props }: CellContentProps) {
	return (
		<div
			className={cx('flex h-full w-full items-center', className)}
			{...props}
		/>
	)
}

export function CellPlaceholder() {
	return (
		<CellContent>
			<span className="text-ui-fg-muted">-</span>
		</CellContent>
	)
}

interface CellTextProps {
	text?: ReactNode
	align?: 'left' | 'center' | 'right'
	maxWidth?: number
	asLink?: boolean
}

export function CellText({
	text,
	align = 'left',
	maxWidth = 220,
	asLink,
}: CellTextProps) {
	if (!text && typeof text !== 'number') {
		return <CellPlaceholder />
	}

	return (
		<div
			className={cx('flex h-full w-full items-center gap-x-3 overflow-hidden', {
				'justify-start text-start': align === 'left',
				'justify-center text-center': align === 'center',
				'justify-end text-end': align === 'right',
				'cursor-pointer text-nowrap text-ui-fg-interactive underline hover:text-ui-fg-interactive-hover':
					asLink,
			})}
			style={{
				maxWidth: maxWidth,
			}}
		>
			{ReactIs.isElement(text) ? (
				text
			) : (
				<span className="truncate">{text}</span>
			)}
		</div>
	)
}

interface CellTextHeaderProps {
	text?: string | number
	align?: 'left' | 'center' | 'right'
}

export function CellTextHeader({ text, align = 'left' }: CellTextHeaderProps) {
	return (
		<div
			className={cx('flex h-full w-full items-center', {
				'justify-start text-start': align === 'left',
				'justify-center text-center': align === 'center',
				'justify-end text-end': align === 'right',
			})}
		>
			<span className="truncate">{text}</span>
		</div>
	)
}

export function CellTextDate({ date }: { date?: Nullish<Date | string> }) {
	if (!date) return <CellPlaceholder />
	return <CellText text={formatDate(date, 'dd/MM/yyyy HH:mm')} />
}

export function CellTextIdr({ idr }: { idr?: Nullish<number | string> }) {
	const _idr = Number(idr)
	if (!_idr) return <CellPlaceholder />
	return <CellText text={toIdr(_idr)} />
}
